import React, { useState, useEffect, useContext } from 'react';
import cn from 'classnames';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Loading from 'tbk-components/src/components/Loading';
import Button from 'tbk-components/src/components/Button';
import LocationTile, { ILocationTileProps } from './LocationTile';

/**
 * @useTermData
 * @taxonomy location-type
 */
export interface ILocationType {
	term_id: any;
}

export interface IContext {
	Api: {
		getLocations: (state: any) => Promise<any[]>;
	};
}

export interface ILocationTilesListingProps extends IBaseElementProps {
	/** @noui */
	context: IContext;
	heading?: string;
	locationType?: ILocationType;
	/**
	 * @noUI
	 */
	defaultState?: any;
	defaultLocations?: ILocationTileProps[];
	loadAllLocations?: string;
	noLocationsFound?: string;
}

/**
 * Display all the locations as tiles
 * @block
 * @icon location
 */
const LocationTilesListing: React.FC<ILocationTilesListingProps> =
	withAppContext(
		({
			context,
			className,
			classNames = [],
			heading,
			locationType,
			defaultState,
			defaultLocations,
			loadAllLocations = 'Load All Locations',
			noLocationsFound = 'No locations found.',
		}) => {
			const { Api } = useContext(context);
			const [locations, setLocations] = useState(defaultLocations);
			const [isExpanded, setIsExpanded] = useState(false);
			const [isFromApi, setIsFromApi] = useState(!defaultLocations);

			const definedState = {
				search: '',
				type: locationType?.term_id,
			};

			const state = definedState.type ? definedState : defaultState || {};

			useEffect(() => {
				// Only fetch from API if defaultLocations was not provided
				if (defaultLocations) {
					return;
				}
				Api.getLocations(state).then((allLocations) => {
					setLocations(allLocations);
					setIsFromApi(true);
				});
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, []);

			if (!locations && !defaultLocations) {
				return (
					<div className="location-loader d-flex justify-content-center">
						<Loading />
					</div>
				);
			}

			// Map API response to LocationTile format and sort alphabetically by title (only for API)
			const mappedLocations: ILocationTileProps[] = locations
				.map((location: any) => ({
					title: location.title,
					permalink: location.permalink,
					address: location.address,
					phone: location.phone,
					comingSoon: location.comingSoon,
					storeType: location.storeType || '',
				}));

			// Only sort if locations were fetched from API
			if (isFromApi) {
				mappedLocations.sort((a, b) => {
					const titleA = a.title?.toLowerCase() || '';
					const titleB = b.title?.toLowerCase() || '';
					return titleA.localeCompare(titleB);
				});
			}

			const initialDisplayCount = 12;
			const showLoadMore =
				mappedLocations.length > initialDisplayCount && !isExpanded;

			return (
				<div
					className={
						className ||
						cn('location-tiles-listing', ...classNames)
					}
				>
					{heading && (
						<h2 className="location-tiles-listing__heading">
							{heading}
						</h2>
					)}
					{(mappedLocations.length > 0 || defaultLocations.length > 0) ? (
						<>
							<div
								className={cn(
									'location-tiles-listing__grid',
									!isExpanded &&
										mappedLocations.length >
											initialDisplayCount &&
										'location-tiles-listing__grid--collapsed',
								)}
							>
								{defaultLocations && !isFromApi
									? defaultLocations.map(
										(location, index) => (
											<LocationTile
												key={index}
												{...location}
											/>
										),
									  )
									:
									mappedLocations.map((location, index) => (
										<LocationTile key={index} {...location} />
									))}
							</div>
							{showLoadMore && (
								<div className="location-tiles-listing__load-more">
									<Button
										onClick={() => setIsExpanded(true)}
										classNames={[cn(
											'location-tiles-listing__load-more-btn',
										)]}
									>
										{loadAllLocations} ({mappedLocations.length})
									</Button>
								</div>
							)}
						</>
					) : (
						<div className="text-center p-5">
							{noLocationsFound}
						</div>
					)}
				</div>
			);
		},
	);

export default LocationTilesListing;
